import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  TrendingUp, 
  TrendingDown, 
  Visibility, 
  TouchApp, 
  Schedule,
  Refresh,
  Analytics as AnalyticsIcon,
  Cloud,
  Storage
} from '@mui/icons-material';
import Layout from '../components/Layout';

import apiService from '../services/api';

const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#F7FAFC',
}));

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: 280, // Width of main sidebar (80px) + websites submenu (200px)
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: '#1A202C',
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const MetricCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 12,
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  border: '1px solid #E2E8F0',
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: '#1A202C',
  marginBottom: theme.spacing(1),
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#718096',
  fontWeight: 500,
}));

const MetricChange = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
}));

const StyledTable = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  border: '1px solid #E2E8F0',
}));

const EnvironmentToggle = styled(ToggleButtonGroup)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 500,
    minWidth: 100,
  },
}));

const EnvironmentIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(1),
  backgroundColor: '#F3F4F6',
  border: '1px solid #E5E7EB',
  fontSize: '0.875rem',
  color: '#6B7280',
}));

interface AnalyticsData {
  pageViews: number;
  uniqueVisitors: number;
  bounceRate: number;
  avgSessionDuration: number;
  topPages: Array<{
    page: string;
    views: number;
    uniqueViews: number;
    avgTimeOnPage: number;
  }>;
  topEvents: Array<{
    event: string;
    count: number;
    conversionRate: number;
  }>;
  conversions: Array<{
    type: string;
    count: number;
    value: number;
  }>;
}

interface GoogleAnalyticsResponse {
  pageViews: number;
  uniqueVisitors: number;
  bounceRate: number;
  avgSessionDuration: number;
  topPages: Array<{
    page: string;
    views: number;
    uniqueViews: number;
    avgTimeOnPage: number;
  }>;
  topEvents: Array<{
    event: string;
    count: number;
    conversionRate: number;
  }>;
  conversions: Array<{
    type: string;
    count: number;
    value: number;
  }>;
}

const AnalyticsPage: React.FC = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [environment, setEnvironment] = useState<'development' | 'staging' | 'production'>('development');
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Set the environment in the API service
      apiService.setEnvironment(environment);
      
      // Call the backend API to fetch Google Analytics data
      const data: GoogleAnalyticsResponse = await apiService.getAnalyticsDashboard(timeRange);
      setAnalyticsData(data);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics data');
      
      // Fallback to mock data if API is not available
      const fallbackData: AnalyticsData = {
        pageViews: 0,
        uniqueVisitors: 0,
        bounceRate: 0,
        avgSessionDuration: 0,
        topPages: [],
        topEvents: [],
        conversions: []
      };
      setAnalyticsData(fallbackData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange, environment]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };



  const getChangeIcon = (change: number) => {
    return change >= 0 ? <TrendingUp /> : <TrendingDown />;
  };

  const getEnvironmentIcon = () => {
    switch (environment) {
      case 'development':
        return <Storage sx={{ fontSize: 16 }} />;
      case 'staging':
        return <Cloud sx={{ fontSize: 16 }} />;
      case 'production':
        return <Cloud sx={{ fontSize: 16, color: '#10B981' }} />;
      default:
        return <Storage sx={{ fontSize: 16 }} />;
    }
  };

  const getEnvironmentColor = () => {
    switch (environment) {
      case 'development':
        return '#6B7280';
      case 'staging':
        return '#F59E0B';
      case 'production':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Layout>
          <MainContent>
            <LinearProgress />
          </MainContent>
        </Layout>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Layout>
          <MainContent>
          <Alert severity="error" sx={{ marginBottom: 3 }}>
            {error}
            <br />
            <Typography variant="body2" sx={{ marginTop: 1 }}>
              Make sure the analytics backend service is running. The analytics data will appear once the service is available.
            </Typography>
          </Alert>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
            <PageTitle>
              <AnalyticsIcon sx={{ fontSize: 32, color: '#6B46C1' }} />
              PropTrak Marketing Analytics
            </PageTitle>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <EnvironmentToggle
                value={environment}
                exclusive
                onChange={(e, newEnvironment) => newEnvironment && setEnvironment(newEnvironment)}
                size="small"
              >
                <ToggleButton value="development">
                  <Storage sx={{ fontSize: 16, mr: 1 }} />
                  Dev
                </ToggleButton>
                <ToggleButton value="staging">
                  <Cloud sx={{ fontSize: 16, mr: 1 }} />
                  Staging
                </ToggleButton>
                <ToggleButton value="production">
                  <Cloud sx={{ fontSize: 16, mr: 1 }} />
                  Prod
                </ToggleButton>
              </EnvironmentToggle>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={timeRange}
                  label="Time Range"
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  <MenuItem value="1d">Last 24 hours</MenuItem>
                  <MenuItem value="7d">Last 7 days</MenuItem>
                  <MenuItem value="30d">Last 30 days</MenuItem>
                  <MenuItem value="90d">Last 90 days</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title="Refresh Data">
                <IconButton onClick={fetchAnalyticsData}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Paper sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              No Analytics Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
              Analytics data will appear here once the backend service is connected to Google Analytics.
            </Typography>
            <Button 
              variant="contained" 
              onClick={fetchAnalyticsData}
              startIcon={<Refresh />}
            >
              Retry Connection
            </Button>
          </Paper>
        </MainContent>
        </Layout>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Layout>
      <MainContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
          <PageTitle>
            <AnalyticsIcon sx={{ fontSize: 32, color: '#6B46C1' }} />
            PropTrak Marketing Analytics
          </PageTitle>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <EnvironmentToggle
              value={environment}
              exclusive
              onChange={(e, newEnvironment) => newEnvironment && setEnvironment(newEnvironment)}
              size="small"
            >
              <ToggleButton value="development">
                <Storage sx={{ fontSize: 16, mr: 1 }} />
                Dev
              </ToggleButton>
              <ToggleButton value="staging">
                <Cloud sx={{ fontSize: 16, mr: 1 }} />
                Staging
              </ToggleButton>
              <ToggleButton value="production">
                <Cloud sx={{ fontSize: 16, mr: 1 }} />
                Prod
              </ToggleButton>
            </EnvironmentToggle>
            <EnvironmentIndicator>
              {getEnvironmentIcon()}
              <span style={{ color: getEnvironmentColor() }}>
                {environment.charAt(0).toUpperCase() + environment.slice(1)}
              </span>
            </EnvironmentIndicator>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="1d">Last 24 hours</MenuItem>
                <MenuItem value="7d">Last 7 days</MenuItem>
                <MenuItem value="30d">Last 30 days</MenuItem>
                <MenuItem value="90d">Last 90 days</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refresh Data">
              <IconButton onClick={fetchAnalyticsData}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Key Metrics */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
          gap: 3,
          marginBottom: 4
        }}>
          <MetricCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <MetricValue>{formatNumber(analyticsData!.pageViews)}</MetricValue>
                  <MetricLabel>Page Views</MetricLabel>
                  <MetricChange>
                    {getChangeIcon(0)}
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                      Live data
                    </Typography>
                  </MetricChange>
                </Box>
                <Visibility sx={{ color: '#6B46C1', fontSize: 40 }} />
              </Box>
            </CardContent>
          </MetricCard>

          <MetricCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <MetricValue>{formatNumber(analyticsData!.uniqueVisitors)}</MetricValue>
                  <MetricLabel>Unique Visitors</MetricLabel>
                  <MetricChange>
                    {getChangeIcon(0)}
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                      Live data
                    </Typography>
                  </MetricChange>
                </Box>
                <TouchApp sx={{ color: '#6B46C1', fontSize: 40 }} />
              </Box>
            </CardContent>
          </MetricCard>

          <MetricCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <MetricValue>{formatPercentage(analyticsData!.bounceRate)}</MetricValue>
                  <MetricLabel>Bounce Rate</MetricLabel>
                  <MetricChange>
                    {getChangeIcon(0)}
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                      Live data
                    </Typography>
                  </MetricChange>
                </Box>
                <TrendingDown sx={{ color: '#6B46C1', fontSize: 40 }} />
              </Box>
            </CardContent>
          </MetricCard>

          <MetricCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <MetricValue>{formatDuration(analyticsData!.avgSessionDuration)}</MetricValue>
                  <MetricLabel>Avg. Session Duration</MetricLabel>
                  <MetricChange>
                    {getChangeIcon(0)}
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                      Live data
                    </Typography>
                  </MetricChange>
                </Box>
                <Schedule sx={{ color: '#6B46C1', fontSize: 40 }} />
              </Box>
            </CardContent>
          </MetricCard>
        </Box>

        {/* Top Pages and Events */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
          gap: 3 
        }}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600 }}>
              Top Pages
            </Typography>
            {analyticsData!.topPages.length > 0 ? (
              <StyledTable>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Page</TableCell>
                      <TableCell align="right">Views</TableCell>
                      <TableCell align="right">Unique Views</TableCell>
                      <TableCell align="right">Avg. Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData!.topPages.map((page, index) => (
                      <TableRow key={index}>
                        <TableCell>{page.page}</TableCell>
                        <TableCell align="right">{formatNumber(page.views)}</TableCell>
                        <TableCell align="right">{formatNumber(page.uniqueViews)}</TableCell>
                        <TableCell align="right">{formatDuration(page.avgTimeOnPage)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </StyledTable>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No page data available
              </Typography>
            )}
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600 }}>
              Top Events
            </Typography>
            {analyticsData!.topEvents.length > 0 ? (
              <StyledTable>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Event</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">Conversion Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData!.topEvents.map((event, index) => (
                      <TableRow key={index}>
                        <TableCell>{event.event}</TableCell>
                        <TableCell align="right">{formatNumber(event.count)}</TableCell>
                        <TableCell align="right">{formatPercentage(event.conversionRate)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </StyledTable>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No event data available
              </Typography>
            )}
          </Paper>
        </Box>

        {/* Conversions */}
        <Paper sx={{ p: 3, borderRadius: 3, marginTop: 3 }}>
          <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600 }}>
            Conversions
          </Typography>
          {analyticsData!.conversions.length > 0 ? (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
              gap: 2 
            }}>
              {analyticsData!.conversions.map((conversion, index) => (
                <Card key={index} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#6B46C1' }}>
                    {formatNumber(conversion.count)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#718096', marginBottom: 1 }}>
                    {conversion.type}
                  </Typography>
                  <Chip 
                    label={`$${formatNumber(conversion.value)}`} 
                    color="success" 
                    size="small" 
                  />
                </Card>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No conversion data available
            </Typography>
          )}
        </Paper>
      </MainContent>
      </Layout>
    </PageContainer>
  );
};

export default AnalyticsPage; 